import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { requireSuccess } from '../../shared/lib/requireSuccess';
import { createGroup, deleteGroup, renameGroup, updateGroupDevices } from '../services/groupsService';
import { realtimeKeys } from './queries';
import {
    addRealTime,
    toggleRealtimeAppNotification,
    toggleRealtimeArchive,
    toggleRealtimeEmailNotification,
    toggleRealtimeLineNotification,
    updateRealtime,
} from './realtimeApi';

// Not awaited, so a caller's snackbar doesn't wait for the refetch.
const refreshLists = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: realtimeKeys.lists() });
};

/** Registers devices for real-time monitoring. */
export const useAddRealtime = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (deviceIds: string[]) => requireSuccess(await addRealTime(deviceIds)),
        onSuccess: () => refreshLists(queryClient),
    });
};

/** Moves a device between the Active and Archive tabs. */
export const useToggleArchive = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ deviceId, archived }: { deviceId: string; archived: boolean }) =>
            requireSuccess(await toggleRealtimeArchive(deviceId, archived)),
        onSuccess: () => refreshLists(queryClient),
    });
};

interface AlarmConfigVariables {
    deviceId: string;
    config: Record<string, unknown>;
    notifications: { app: boolean; email: boolean; line: boolean };
}

/** The thresholds were saved, but at least one notification switch wasn't. */
export class NotificationsNotSavedError extends Error {}

/** Saves a device's thresholds, then its three notification switches. */
export const useUpdateAlarmConfig = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ deviceId, config, notifications }: AlarmConfigVariables) => {
            requireSuccess(await updateRealtime([deviceId], config));
            const toggles = await Promise.allSettled([
                toggleRealtimeAppNotification(deviceId, notifications.app),
                toggleRealtimeEmailNotification(deviceId, notifications.email),
                toggleRealtimeLineNotification(deviceId, notifications.line),
            ]);
            if (toggles.some((r) => r.status === 'rejected' || r.value?.status !== 'success')) {
                throw new NotificationsNotSavedError('notification settings not saved');
            }
        },
        // Settled, not success: the thresholds may have saved even when a switch didn't.
        onSettled: () => refreshLists(queryClient),
    });
};

/** Renames a device; the card shows the new name at once and reverts if the save fails. */
export const useRenameDevice = () => {
    const queryClient = useQueryClient();
    const lists = realtimeKeys.lists();

    return useMutation({
        mutationFn: async ({ deviceId, name }: { deviceId: string; name: string }) =>
            requireSuccess(await updateRealtime([deviceId], { name })),
        onMutate: async ({ deviceId, name }) => {
            await queryClient.cancelQueries({ queryKey: lists });
            const snapshots = queryClient.getQueriesData<any[]>({ queryKey: lists });
            queryClient.setQueriesData<any[]>({ queryKey: lists }, (records) =>
                records?.map((record) => (record.device_id === deviceId ? { ...record, name } : record))
            );
            return { snapshots };
        },
        onError: (_error, _variables, context) => {
            context?.snapshots.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
        },
        onSettled: () => refreshLists(queryClient),
    });
};

/** Saves card positions after a drop. A failure is only logged; the next poll shows the stored order. */
export const useSaveCardOrder = () =>
    useMutation({
        mutationFn: async (changes: { deviceId: string; order: number }[]) => {
            await Promise.all(changes.map(({ deviceId, order }) => updateRealtime([deviceId], { order })));
        },
    });

// Use the group list the response echoes back (the list endpoint fails for vendor accounts).
const applyGroups = (queryClient: QueryClient, groups: any[] | null) => {
    if (groups) queryClient.setQueryData(realtimeKeys.groups(), groups);
    else queryClient.invalidateQueries({ queryKey: realtimeKeys.groups() });
};

/** Resolves { group, duplicate }; group is null when the name was rejected. */
export const useCreateGroup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (name: string) => createGroup(name),
        onSuccess: ({ group, groups }) => {
            if (group?.id) applyGroups(queryClient, groups);
        },
    });
};

/** Resolves { ok, duplicate }. */
export const useRenameGroup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, name }: { id: string; name: string }) => renameGroup(id, name),
        onSuccess: ({ ok, groups }) => {
            if (ok) applyGroups(queryClient, groups);
        },
    });
};

export const useDeleteGroup = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => deleteGroup(id),
        onSuccess: ({ groups }) => applyGroups(queryClient, groups),
    });
};

/** Adds and removes group members; awaits the refetch so the grid is current when the dialog closes. */
export const useUpdateGroupDevices = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ groupId, add, remove }: { groupId: string; add: string[]; remove: string[] }) =>
            updateGroupDevices(groupId, { add, remove }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: realtimeKeys.lists() }),
    });
};
